/**
 * Document Naming Utilities
 * 
 * Provides token-based file naming for generated documents.
 * 
 * Available tokens:
 * - {client} - Full client name
 * - {client_short} - Client short name (falls back to full name if not set)
 * - {project} - Project title (from contract.title)
 * - {template} - Template name
 * - {date} - Full date (YYYY-MM-DD)
 * - {year} - Year (YYYY)
 * - {month} - Month (MM)
 * - {day} - Day (DD)
 * - {timestamp} - Unix timestamp
 * 
 * Example patterns:
 * - "{client_short}_{template}_{date}" → "MS_PSA_2025-10-11"
 * - "{client}_SOW_{year}_{month}" → "Microsoft_SOW_2025_10"
 * - "SOW_{project}_{date}" → "SOW_SharePointImplementation_2025-10-11"
 */

interface NamingContext {
  clientName: string;
  clientShortName?: string;
  projectTitle?: string;
  templateName: string;
  templateType?: string;
  date?: Date;
}

/**
 * Generates a filename from a naming pattern and context
 */
export function generateDocumentFilename(
  pattern: string,
  context: NamingContext
): string {
  const date = context.date || new Date();
  
  // Prepare token values
  const tokens: Record<string, string> = {
    client: sanitizeFilename(context.clientName),
    client_short: sanitizeFilename(context.clientShortName || context.clientName),
    project: sanitizeFilename(context.projectTitle || ''),
    template: sanitizeFilename(context.templateType || context.templateName),
    date: formatDate(date, 'YYYY-MM-DD'),
    year: formatDate(date, 'YYYY'),
    month: formatDate(date, 'MM'),
    day: formatDate(date, 'DD'),
    timestamp: date.getTime().toString(),
  };

  // Replace tokens in pattern
  let filename = pattern;
  for (const [token, value] of Object.entries(tokens)) {
    const regex = new RegExp(`\\{${token}\\}`, 'g');
    filename = filename.replace(regex, value);
  }

  // Clean up the filename
  filename = sanitizeFilename(filename);
  
  // Ensure .docx extension
  if (!filename.toLowerCase().endsWith('.docx')) {
    filename += '.docx';
  }

  return filename;
}

/**
 * Sanitizes a string for use in filenames
 */
function sanitizeFilename(str: string): string {
  if (!str) return '';
  
  return str
    .trim()
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Remove invalid filename characters
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    // Remove multiple consecutive underscores
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .replace(/^_+|_+$/g, '');
}

/**
 * Formats a date according to the specified format
 */
function formatDate(date: Date, format: string): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'YYYY':
      return String(year);
    case 'MM':
      return month;
    case 'DD':
      return day;
    default:
      return `${year}-${month}-${day}`;
  }
}

/**
 * Validates a naming pattern
 */
export function validateNamingPattern(pattern: string): { valid: boolean; error?: string } {
  if (!pattern || pattern.trim().length === 0) {
    return { valid: false, error: 'Pattern cannot be empty' };
  }

  // Check for valid tokens
  const tokenRegex = /\{([^}]+)\}/g;
  const matches = pattern.matchAll(tokenRegex);
  const validTokens = ['client', 'client_short', 'project', 'template', 'date', 'year', 'month', 'day', 'timestamp'];
  
  for (const match of matches) {
    const token = match[1];
    if (!validTokens.includes(token)) {
      return { valid: false, error: `Invalid token: {${token}}. Valid tokens: ${validTokens.join(', ')}` };
    }
  }

  // Check for invalid characters
  const invalidChars = /[<>:"/\\|?*\x00-\x1F]/g;
  // Remove tokens temporarily to check the rest
  const withoutTokens = pattern.replace(tokenRegex, '');
  if (invalidChars.test(withoutTokens)) {
    return { valid: false, error: 'Pattern contains invalid filename characters' };
  }

  return { valid: true };
}

/**
 * Gets example filename from pattern
 */
export function getExampleFilename(pattern: string): string {
  const example = generateDocumentFilename(pattern, {
    clientName: 'Microsoft Corporation',
    clientShortName: 'MS',
    projectTitle: 'SharePoint Implementation',
    templateName: 'Statement of Work',
    templateType: 'SOW',
    date: new Date('2025-10-11'),
  });
  
  return example;
}

/**
 * Default naming patterns by template type
 */
export const DEFAULT_NAMING_PATTERNS: Record<string, string> = {
  SOW: '{client_short}_SOW_{date}',
  PSA: '{client_short}_PSA_{date}',
  NDA: '{client_short}_NDA_{date}',
  MSA: '{client_short}_MSA_{date}',
  default: '{client_short}_{template}_{date}',
};

